/**
 * «نسيت كلمة السر؟» (المرحلة 1.1, F01).
 *
 * Three steps on one screen: which account, the six-digit code it was sent,
 * and the new password. The code goes by email or SMS — whichever the
 * platform's `passwordReset` policy allows and the account can be reached on —
 * and the email carries a link too, which opens ResetPasswordPage below with
 * the code already spent from the link.
 *
 * The server answers the first step the same way whether or not the account
 * exists, so the screen never says "no such account"; it says where the code
 * went only when the server says so. A new password ends every session the
 * account had.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Field, Icon, Input, Panel } from '../../components/common';
import { StepStrip } from '../../components/common/Tile3D';
import { AgencyMark, ProductMark } from '../../components/layout/BrandMarks';
import { authApi } from '../../api/endpoints';
import { useI18n } from '../../i18n/I18nProvider';
import { passwordStrength } from '../../domain/identity';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';

const RESEND_SECONDS = 45;

function AuthFrame({ title, icon, children }) {
  return (
    <div className="auth-screen app-backdrop flex min-h-screen items-center justify-center p-4">
      <div className="auth-screen__lang">
        <LanguageSwitch />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <AgencyMark variant="hero" />
        </div>
        <Panel className="auth-card border-t-4 border-t-cyan-500 shadow-2xl shadow-cyan-500/10">
          <div className="mb-5 flex flex-col items-center text-center">
            <ProductMark variant="card" />
            <h1 className="flex items-center gap-2 text-xl font-bold text-white">
              <Icon name={icon} size={20} className="text-cyan-400" />
              {title}
            </h1>
          </div>
          {children}
        </Panel>
      </div>
    </div>
  );
}

/** Six boxes, one digit each; a pasted code fills them all. */
export function CodeBoxes({ value, onChange, autoFocus = false, label }) {
  const refs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const setAt = (index, digit) => {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join('').replace(/\s/g, ''));
  };

  const onInput = (index) => (event) => {
    const raw = event.target.value.replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))).replace(/\D/g, '');
    if (raw.length > 1) {
      onChange(raw.slice(0, 6));
      refs.current[Math.min(5, raw.length)]?.focus();
      return;
    }
    setAt(index, raw);
    if (raw && index < 5) refs.current[index + 1]?.focus();
  };

  const onKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) refs.current[index - 1]?.focus();
  };

  return (
    <div className="otp" role="group" aria-label={label}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={digit}
          onChange={onInput(i)}
          onKeyDown={onKeyDown(i)}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={6}
          autoFocus={autoFocus && i === 0}
          className={`otp__box ${digit ? 'is-filled' : ''}`}
          aria-label={`${label} ${i + 1}`}
          data-testid={`otp-${i}`}
        />
      ))}
    </div>
  );
}

/** The new password twice, with its strength. */
function NewPasswordFields({ value, confirm, onValue, onConfirm, error }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const strength = passwordStrength(value);
  const strengthLabel = strength >= 3 ? t('forgot.strong') : strength === 2 ? t('forgot.fair') : t('forgot.weak');
  return (
    <div className="space-y-4">
      <Field label={t('forgot.newPassword')} required error={error}>
        <div className="relative">
          <Input type={show ? 'text' : 'password'} value={value} onChange={(e) => onValue(e.target.value)} autoComplete="new-password" className="!pe-11" data-testid="reset-password" />
          <button type="button" onClick={() => setShow((v) => !v)} className="absolute end-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 hover:text-cyan-400" aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}>
            <Icon name={show ? 'eye-off' : 'eye'} size={17} />
          </button>
        </div>
      </Field>
      {value && (
        <div className="-mt-2 space-y-1">
          <div className={`strength strength--${strength}`} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="strength__bar" />
            ))}
          </div>
          <p className="text-[11px] text-slate-400">
            {t('forgot.strength')}: <b className="text-slate-200">{strengthLabel}</b>
          </p>
        </div>
      )}
      <Field label={t('forgot.confirmPassword')} required>
        <Input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => onConfirm(e.target.value)} autoComplete="new-password" data-testid="reset-confirm" />
      </Field>
    </div>
  );
}

function checkPasswords(t, value, confirm) {
  if (value.length < 6) return t('forgot.short');
  if (value !== confirm) return t('forgot.mismatch');
  return null;
}

export function ForgotPasswordPage() {
  const { t, policy, isRtl } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [identifier, setIdentifier] = useState(location.state?.identifier || '');
  const [channel, setChannel] = useState('email');
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  const via = policy?.passwordReset || 'both';
  const channels = useMemo(() => ({ admin: [], email: ['email'], sms: ['sms'], both: ['email', 'sms'] })[via] || ['email', 'sms'], [via]);

  useEffect(() => {
    if (channels.length && !channels.includes(channel)) setChannel(channels[0]);
  }, [channels, channel]);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const send = async (event) => {
    event?.preventDefault();
    if (!identifier.trim()) {
      setError(t('auth.needIdentifier'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const answer = await authApi.forgotPassword(identifier.trim(), channel);
      setSent(answer);
      setStep(1);
      setWait(RESEND_SECONDS);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toPassword = (event) => {
    event.preventDefault();
    if (code.length !== 6) {
      setError(t('forgot.codeHint'));
      return;
    }
    setError(null);
    setStep(2);
  };

  const reset = async (event) => {
    event.preventDefault();
    const problem = checkPasswords(t, password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authApi.resetPassword({ identifier: identifier.trim(), code, newPassword: password });
      setStep(3);
    } catch (err) {
      // A wrong or spent code sends the person back to it, not to the start.
      if (err.body?.field === 'code') {
        setError(t('forgot.invalidCode'));
        setCode('');
        setStep(1);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (via === 'admin') {
    return (
      <AuthFrame title={t('forgot.title')} icon="key-round">
        <div className="space-y-4 text-center">
          <span className="tile3d__face tone-amber mx-auto" style={{ width: 56, height: 56 }}>
            <Icon name="user-round-check" size={26} className="tile3d__icon" />
          </span>
          <p className="text-sm leading-relaxed text-slate-300">{t('forgot.adminOnly')}</p>
          <Link to="/login" className="btn-ghost w-full">
            {t('forgot.backToLogin')}
          </Link>
        </div>
      </AuthFrame>
    );
  }

  const steps = [t('forgot.step1'), t('forgot.step2'), t('forgot.step3')];

  return (
    <AuthFrame title={t('forgot.title')} icon="key-round">
      {step < 3 && (
        <div className="flex justify-center">
          <StepStrip steps={steps} current={step} />
        </div>
      )}

      {step === 0 && (
        <form onSubmit={send} className="space-y-4" noValidate>
          <p className="text-sm leading-relaxed text-slate-300">{t('forgot.intro')}</p>
          <Field label={t('forgot.identifier')} required>
            <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoFocus autoComplete="username" dir="auto" data-testid="forgot-identifier" />
          </Field>
          {channels.length > 1 && (
            <div>
              <p className="mb-2 text-xs font-bold text-slate-300">{t('forgot.channel')}</p>
              <div className="choice-cards">
                {channels.map((option) => (
                  <button key={option} type="button" onClick={() => setChannel(option)} className={`choice-card ${channel === option ? 'is-on' : ''}`} aria-pressed={channel === option} data-testid={`channel-${option}`}>
                    <span className={`tile3d__face ${option === 'email' ? 'tone-cyan' : 'tone-emerald'}`} style={{ width: 34, height: 34 }}>
                      <Icon name={option === 'email' ? 'mail' : 'smartphone'} size={16} className="tile3d__icon" />
                    </span>
                    {option === 'email' ? t('forgot.email') : t('forgot.sms')}
                  </button>
                ))}
              </div>
            </div>
          )}
          {error && <ErrorLine text={error} />}
          <Button type="submit" className="w-full" icon="send" loading={busy} data-testid="forgot-send">
            {t('forgot.send')}
          </Button>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={toPassword} className="space-y-4" noValidate>
          <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4 text-center">
            <Icon name={sent?.channel === 'sms' ? 'smartphone' : 'mail-check'} size={26} className="mx-auto mb-1.5 text-cyan-400" />
            <p className="text-sm font-bold text-white">{t('forgot.sentTitle')}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">
              {sent?.destination ? t('forgot.sentTo', { to: sent.destination }) : t('forgot.sentGeneric')}
            </p>
          </div>
          <div>
            <p className="mb-2 text-center text-xs font-bold text-slate-300">{t('forgot.code')}</p>
            <CodeBoxes value={code} onChange={setCode} autoFocus label={t('forgot.code')} />
          </div>
          {error && <ErrorLine text={error} />}
          <Button type="submit" className="w-full" icon={isRtl ? 'arrow-left' : 'arrow-right'} disabled={code.length !== 6} data-testid="forgot-next">
            {t('common.next')}
          </Button>
          <button type="button" disabled={wait > 0 || busy} onClick={send} className="w-full text-center text-xs font-bold text-cyan-400 disabled:text-slate-500">
            {wait > 0 ? t('forgot.resendIn', { s: wait }) : t('forgot.resend')}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={reset} className="space-y-4" noValidate>
          <NewPasswordFields value={password} confirm={confirm} onValue={setPassword} onConfirm={setConfirm} />
          {error && <ErrorLine text={error} />}
          <Button type="submit" className="w-full" icon="key-round" loading={busy} data-testid="forgot-reset">
            {t('forgot.reset')}
          </Button>
        </form>
      )}

      {step === 3 && <DoneBlock onLogin={() => navigate('/login', { replace: true, state: { notice: 'auth.resetDone' } })} />}

      {step < 3 && (
        <p className="mt-5 text-center text-xs">
          <Link to="/login" className="font-bold text-slate-400 hover:text-cyan-300">
            {t('forgot.backToLogin')}
          </Link>
        </p>
      )}
    </AuthFrame>
  );
}

/** /reset-password?token=… — the link in the email. */
export function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const problem = checkPasswords(t, password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authApi.resetPassword({ token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err.body?.field === 'code' ? t('forgot.linkExpired') : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={t('forgot.linkTitle')} icon="key-round">
      {done ? (
        <DoneBlock onLogin={() => navigate('/login', { replace: true, state: { notice: 'auth.resetDone' } })} />
      ) : !token ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-slate-300">{t('forgot.sentGeneric')}</p>
          <Link to="/forgot-password" className="btn-primary w-full">
            {t('forgot.send')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          <NewPasswordFields value={password} confirm={confirm} onValue={setPassword} onConfirm={setConfirm} />
          {error && <ErrorLine text={error} />}
          <Button type="submit" className="w-full" icon="key-round" loading={busy}>
            {t('forgot.reset')}
          </Button>
          <p className="text-center text-xs">
            <Link to="/forgot-password" className="font-bold text-cyan-400">
              {t('forgot.resend')}
            </Link>
          </p>
        </form>
      )}
    </AuthFrame>
  );
}

function ErrorLine({ text }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300" role="alert">
      <Icon name="alert-circle" size={14} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function DoneBlock({ onLogin }) {
  const { t } = useI18n();
  return (
    <div className="space-y-4 text-center" data-testid="reset-done">
      <span className="tile3d__face tone-emerald mx-auto" style={{ width: 64, height: 64 }}>
        <Icon name="circle-check" size={30} className="tile3d__icon" />
      </span>
      <p className="text-sm leading-relaxed text-slate-200">{t('forgot.done')}</p>
      <Button className="w-full" icon="log-in" onClick={onLogin}>
        {t('forgot.backToLogin')}
      </Button>
    </div>
  );
}

export default ForgotPasswordPage;
